import type { TooltipOptions } from 'react-laag';
import { useTooltip } from 'react-laag';
import { DEFAULT_PLACEMENT_CONFIG } from '../../util';
import * as React from 'react';
import { isTouchableDevice } from '../../util/DeviceUtil';

/**
 * Usage specific custom options for {@link useTooltipDefault}.
 */
export interface DefaultTooltipOptions {
  /**
   * Custom options to pass to {@link useTooltipDefault}. This should only be needed in very rare scenarios.
   */
  tooltipOptions?: Partial<Omit<TooltipOptions, 'ResizeObserver'>>;
}

/**
 * Hook for creating a tooltip for a component with the default configuration.
 * This hook simply uses the {@link useTooltip} hook of react-laag with some default configurations.
 *
 * @param tooltip the tooltip to display
 * @param options custom options to pass to {@link useTooltip}. When using this, please think about if what you need
 * is really specific to your usage or if the default should be adjusted.
 * @return see {@link useTooltip}
 */
export function useTooltipDefault(
  tooltip?: string,
  options?: DefaultTooltipOptions['tooltipOptions']
): ReturnType<typeof useTooltip> {
  return useTooltip(
    ({ isOpen, layerProps }) =>
      tooltip &&
      !isTouchableDevice() &&
      isOpen && (
        <div {...layerProps} className="ehs-tooltip">
          {tooltip}
        </div>
      ),
    {
      delayEnter: 500,
      delayLeave: 100,
      placement: DEFAULT_PLACEMENT_CONFIG,
      ...options
    }
  );
}
